import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { fmtDate } from "./shared";
import {
    Archive, ArchiveRestore, Briefcase, Calendar, Check, ChevronRight, ClipboardCheck,
    Download, FileText, Landmark, Percent, Plus, Search, Trash2, Upload,
    User, UserCheck, Home as HomeIcon, X, Save, Euro, ScrollText,
} from "lucide-react";

const DOC_FOLDER_LABELS = {
    seller: { label: "Venditore", icon: User },
    buyer: { label: "Acquirente", icon: UserCheck },
    property: { label: "Immobile", icon: HomeIcon },
};

const MORTGAGE_STATUSES = [
    { id: "", label: "—" },
    { id: "non_richiesto", label: "Non richiesto" },
    { id: "in_valutazione", label: "In valutazione" },
    { id: "approvato", label: "Approvato" },
    { id: "erogato", label: "Erogato" },
    { id: "rifiutato", label: "Rifiutato" },
];

const contactLabel = (c) => c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.phone || "—" : "—";
const propertyLabel = (p) => p ? `${p.title || "Immobile"} · ${p.city || ""}` : "—";

// Formattazione EUR con 2 decimali e locale italiano (es. 4.000,00 €)
const formatEUR2 = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(n));
};

export default function CrmDeals() {
    const [tab, setTab] = useState("in_corso");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [summary, setSummary] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [loadError, setLoadError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const [r, s] = await Promise.all([
                api.get("/deals", { params: { status: tab, ...(q ? { q } : {}) } }),
                api.get("/deals/summary"),
            ]);
            setItems(r.data.items || []);
            setSummary(s.data);
        } catch (e) {
            setItems([]);
            setSummary(null);
            setLoadError(e?.response?.data?.detail || "Impossibile caricare le pratiche. Riprova.");
        }
        finally { setLoading(false); }
    }, [tab, q]);

    useEffect(() => { load(); }, [load]);

    const openDetail = (id) => setSelectedId(id);
    const closeDetail = () => setSelectedId(null);

    return (
        <div data-testid="crm-deals" className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-end gap-4 justify-between">
                <div>
                    <span className="label-eyebrow text-gold">VD Manager · Compravendite</span>
                    <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                        <Briefcase size={22} className="text-gold" /> Pratiche
                    </h2>
                    <p className="text-ink-400 text-sm mt-1">Segui l&apos;intero ciclo di ogni compravendita: parti, checklist, scadenze, documenti e provvigioni.</p>
                </div>
                <button
                    data-testid="new-deal-btn"
                    onClick={() => setShowNew(true)}
                    className="cta-gold px-4 py-2.5 text-xs flex items-center gap-2"
                >
                    <Plus size={14} /> Nuova pratica
                </button>
            </div>

            {/* Summary widgets */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <SummaryCard label="In corso" value={summary?.in_corso ?? 0} icon={<Briefcase size={22} />} color="blue" testid="summary-in-corso" />
                <SummaryCard label="Archiviate" value={summary?.archiviate ?? 0} icon={<Archive size={22} />} color="violet" testid="summary-archiviate" />
                <SummaryCard label="Provvigioni potenziali" value={formatEUR2(summary?.potential_commission_total || 0)} icon={<Euro size={22} />} color="green" testid="summary-provvigioni" />
                <SummaryCard label="Prossime scadenze" value={summary?.upcoming_deadlines?.length ?? 0} icon={<Calendar size={22} />} color="amber" testid="summary-scadenze" />
            </div>

            {loadError && (
                <div data-testid="deals-load-error" className="border border-red-700/40 bg-red-900/20 text-red-200 text-sm px-4 py-3">
                    {loadError}
                </div>
            )}

            {/* Tabs */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex border border-white/15">
                    <TabPill id="in_corso" active={tab === "in_corso"} onClick={setTab}>In corso</TabPill>
                    <TabPill id="archiviata" active={tab === "archiviata"} onClick={setTab}>Archiviate</TabPill>
                </div>
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                    <input
                        data-testid="deals-search"
                        placeholder="Cerca per titolo, notaio, note..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-ink-100 outline-none focus:border-gold"
                    />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-ink-400 text-sm py-16 text-center">Caricamento pratiche...</div>
            ) : items.length === 0 ? (
                <EmptyState onCreate={() => setShowNew(true)} archived={tab === "archiviata"} />
            ) : (
                <div className="space-y-4">
                    {items.map((d) => (
                        <DealRow key={d.id} deal={d} onOpen={() => openDetail(d.id)} />
                    ))}
                </div>
            )}

            {/* Prossime scadenze widget */}
            {(summary?.upcoming_deadlines?.length ?? 0) > 0 && (
                <div className="border border-white/10 bg-ink-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-wider text-gold mb-3 flex items-center gap-2">
                        <Calendar size={12} /> Prossime scadenze pratiche
                    </div>
                    <ul className="space-y-2 text-sm">
                        {(summary.upcoming_deadlines || []).map((u) => (
                            <li key={u.deadline_id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0">
                                <button onClick={() => openDetail(u.deal_id)} className="text-ink-100 hover:text-gold text-left">
                                    <span className="text-ink-400 text-[11px]">[{u.deal_title}]</span> {u.title}
                                </button>
                                <span className="text-[11px] text-purple-300">{fmtDate(u.date)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {selectedId && (
                <DealDetailDrawer
                    dealId={selectedId}
                    onClose={closeDetail}
                    onChanged={load}
                />
            )}

            {showNew && (
                <NewDealModal
                    onClose={() => setShowNew(false)}
                    onCreated={(newDeal) => { setShowNew(false); load(); setSelectedId(newDeal.id); }}
                />
            )}
        </div>
    );
}

function SummaryCard({ label, value, icon, color = "blue", testid }) {
    const iconCls = color === "green" ? "vd-kpi-icon-highlight" : color === "amber" ? "vd-kpi-icon-amber" : color === "violet" ? "vd-kpi-icon-violet" : "";
    return (
        <div className="vd-kpi-card p-6 flex items-center gap-4" data-testid={testid}>
            <span className={`vd-kpi-icon ${iconCls}`}>{icon}</span>
            <div>
                <div className="text-ink-400 text-[10px] uppercase tracking-wider">{label}</div>
                <div className="font-serif text-2xl text-ink-50 mt-0.5" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
            </div>
        </div>
    );
}

function TabPill({ id, active, onClick, children }) {
    return (
        <button
            data-testid={`deal-tab-${id}`}
            onClick={() => onClick(id)}
            className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${active ? "bg-gold text-ink-950" : "text-ink-300 hover:text-ink-50 hover:bg-white/5"}`}
        >
            {children}
        </button>
    );
}

function EmptyState({ onCreate, archived }) {
    return (
        <div className="border border-dashed border-white/15 p-12 text-center bg-ink-900/20">
            <Briefcase size={30} className="text-gold mx-auto mb-3 opacity-70" />
            <h3 className="font-serif text-lg text-ink-50 mb-2">{archived ? "Nessuna pratica archiviata" : "Nessuna pratica in corso"}</h3>
            <p className="text-ink-400 text-sm mb-4 max-w-md mx-auto">
                {archived
                    ? "Le pratiche completate o interrotte appariranno qui una volta archiviate."
                    : "Crea la prima pratica per iniziare a tracciare venditore, acquirente, checklist, scadenze e documenti in un unico posto."}
            </p>
            {!archived && (
                <button onClick={onCreate} data-testid="empty-create-deal" className="cta-gold px-4 py-2.5 text-xs inline-flex items-center gap-2">
                    <Plus size={14} /> Crea pratica
                </button>
            )}
        </div>
    );
}

function DealRow({ deal, onOpen }) {
    const done = (deal.checklist || []).filter((c) => c.done).length;
    const total = (deal.checklist || []).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const comm = deal.commission || {};
    const commissionDisplay = comm.calculated_amount || comm.fixed_amount;
    return (
        <button
            data-testid={`deal-row-${deal.id}`}
            onClick={onOpen}
            className="vd-card vd-lead-row w-full text-left px-5 py-4 transition-colors flex flex-wrap gap-4 items-center"
        >
            <div className="min-w-0 flex-1">
                <div className="font-serif text-lg text-ink-50 truncate">{deal.title}</div>
                <div className="text-[11px] text-ink-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span><User size={10} className="inline mr-1" />Venditore: {contactLabel(deal._seller)}</span>
                    <span><UserCheck size={10} className="inline mr-1" />Acquirente: {contactLabel(deal._buyer)}</span>
                    <span><HomeIcon size={10} className="inline mr-1" />{propertyLabel(deal._property)}</span>
                </div>
            </div>
            <div className="text-right shrink-0">
                {deal.sale_price ? (
                    <div className="text-price font-serif text-lg">{formatPrice(deal.sale_price)}</div>
                ) : null}
                {commissionDisplay ? (
                    <div className="text-[10px] text-green-300 mt-0.5">Prov. {formatEUR2(commissionDisplay)}</div>
                ) : null}
            </div>
            <div className="w-32 shrink-0">
                <div className="text-[10px] text-ink-400 mb-1 flex justify-between"><span>Avanzamento</span><span>{done}/{total}</span></div>
                <div className="h-1.5 bg-ink-950 relative overflow-hidden">
                    <div className={`absolute inset-y-0 left-0 ${pct === 100 ? "bg-green-400" : "bg-gold"}`} style={{ width: `${pct}%` }} />
                </div>
            </div>
            <ChevronRight size={16} className="text-ink-500 shrink-0" />
        </button>
    );
}

// ============================================================
// New Deal Modal
// ============================================================
function NewDealModal({ onClose, onCreated }) {
    const [refdata, setRefdata] = useState({ contacts: [], properties: [] });
    const [form, setForm] = useState({
        title: "",
        seller_contact_id: "",
        buyer_contact_id: "",
        property_id: "",
        sale_price: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/deals/refdata/parties").then((r) => setRefdata(r.data)).catch(() => {});
    }, []);

    const submit = async () => {
        if (!form.title.trim()) { setError("Titolo obbligatorio"); return; }
        setSaving(true);
        setError("");
        try {
            const payload = {
                title: form.title.trim(),
                seller_contact_id: form.seller_contact_id || null,
                buyer_contact_id: form.buyer_contact_id || null,
                property_id: form.property_id || null,
                sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
            };
            const r = await api.post("/deals", payload);
            onCreated(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore nella creazione");
        } finally { setSaving(false); }
    };

    return (
        <ModalShell title="Nuova pratica" onClose={onClose} testid="new-deal-modal">
            <div className="space-y-4">
                <Field label="Titolo *" required>
                    <input
                        data-testid="new-deal-title"
                        placeholder="Es. Compravendita Rossi - Bianchi (via Roma 12)"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="input-vd"
                        autoFocus
                    />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Venditore">
                        <ContactSelect
                            value={form.seller_contact_id}
                            onChange={(v) => setForm({ ...form, seller_contact_id: v })}
                            contacts={refdata.contacts}
                            testid="new-deal-seller"
                        />
                    </Field>
                    <Field label="Acquirente">
                        <ContactSelect
                            value={form.buyer_contact_id}
                            onChange={(v) => setForm({ ...form, buyer_contact_id: v })}
                            contacts={refdata.contacts}
                            testid="new-deal-buyer"
                        />
                    </Field>
                </div>
                <Field label="Immobile">
                    <select
                        data-testid="new-deal-property"
                        value={form.property_id}
                        onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                        className="input-vd"
                    >
                        <option value="">— Seleziona (opzionale) —</option>
                        {(refdata.properties || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.title} · {p.city} · {formatPrice(p.price)}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Prezzo di vendita (€)">
                    <input
                        data-testid="new-deal-price"
                        type="number"
                        min="0"
                        step="1000"
                        placeholder="Es. 250000"
                        value={form.sale_price}
                        onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                        className="input-vd"
                    />
                </Field>
                {error && <div className="text-red-300 text-xs">{error}</div>}
                <div className="text-[11px] text-ink-500">La checklist standard (6 fasi) verrà creata automaticamente. Puoi personalizzarla nella pratica.</div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="cta-outline px-4 py-2 text-xs">Annulla</button>
                    <button
                        data-testid="new-deal-save"
                        onClick={submit}
                        disabled={saving}
                        className="cta-gold px-5 py-2 text-xs flex items-center gap-2"
                    >
                        <Save size={13} /> {saving ? "Creazione..." : "Crea pratica"}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

function ContactSelect({ value, onChange, contacts, testid }) {
    return (
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="input-vd">
            <option value="">— Seleziona (opzionale) —</option>
            {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                    {contactLabel(c)}{c.city ? ` · ${c.city}` : ""}
                </option>
            ))}
        </select>
    );
}

// ============================================================
// Detail Drawer
// ============================================================
function DealDetailDrawer({ dealId, onClose, onChanged }) {
    const [deal, setDeal] = useState(null);
    const [refdata, setRefdata] = useState({ contacts: [], properties: [] });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [r, rd] = await Promise.all([
                api.get(`/deals/${dealId}`),
                api.get("/deals/refdata/parties"),
            ]);
            setDeal(r.data);
            setRefdata(rd.data);
        } catch { onClose(); }
    }, [dealId, onClose]);

    useEffect(() => { load(); }, [load]);

    const persist = async (patch) => {
        setSaving(true);
        try {
            const r = await api.put(`/deals/${dealId}`, patch);
            setDeal((prev) => ({ ...prev, ...r.data }));
            onChanged?.();
        } catch (e) { window.alert(e?.response?.data?.detail || "Errore salvataggio"); }
        finally { setSaving(false); }
    };

    const archive = async () => {
        if (!window.confirm(deal.status === "archiviata" ? "Ripristinare la pratica tra quelle in corso?" : "Archiviare la pratica? Potrai sempre ripristinarla dalla scheda Archiviate.")) return;
        try {
            await api.post(`/deals/${dealId}/${deal.status === "archiviata" ? "unarchive" : "archive"}`);
            onChanged?.();
            onClose();
        } catch (e) { window.alert(e?.response?.data?.detail || "Errore"); }
    };

    const remove = async () => {
        if (!window.confirm("Eliminare definitivamente questa pratica? L'operazione è irreversibile.")) return;
        try {
            await api.delete(`/deals/${dealId}`);
            onChanged?.();
            onClose();
        } catch (e) { window.alert(e?.response?.data?.detail || "Errore"); }
    };

    return (
        <DrawerShell onClose={onClose} testid="deal-detail-drawer">
            {!deal ? (
                <div className="p-8 text-center text-sm text-ink-400">Caricamento pratica...</div>
            ) : (
                <div className="p-6 space-y-6">
                    <DealHeader deal={deal} onSave={persist} onArchive={archive} onDelete={remove} saving={saving} />
                    <PartiesSection deal={deal} refdata={refdata} onSave={persist} />
                    <CommissionSection deal={deal} onSave={persist} />
                    <ChecklistSection dealId={dealId} deal={deal} onReload={load} />
                    <DeadlinesSection dealId={dealId} deal={deal} onReload={load} />
                    <NotaryMortgageSection deal={deal} onSave={persist} />
                    <DocumentsSection dealId={dealId} deal={deal} onReload={load} />
                    <NotesSection deal={deal} onSave={persist} />
                </div>
            )}
        </DrawerShell>
    );
}

function DealHeader({ deal, onSave, onArchive, onDelete, saving }) {
    const [title, setTitle] = useState(deal.title);
    const [salePrice, setSalePrice] = useState(deal.sale_price ?? "");
    useEffect(() => { setTitle(deal.title); setSalePrice(deal.sale_price ?? ""); }, [deal.id, deal.title, deal.sale_price]);
    const commitTitle = () => { if (title !== deal.title) onSave({ title }); };
    const commitPrice = () => {
        const parsed = salePrice === "" ? null : parseFloat(salePrice);
        if (parsed !== deal.sale_price) onSave({ sale_price: parsed });
    };
    return (
        <div className="space-y-3">
            <div className="flex items-start gap-3 flex-wrap">
                <input
                    data-testid="deal-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={commitTitle}
                    className="flex-1 min-w-[240px] bg-transparent border-b border-white/10 focus:border-gold text-2xl font-serif text-ink-50 py-2 outline-none"
                />
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${deal.status === "archiviata" ? "border-white/15 text-ink-400" : "border-gold/40 text-gold bg-gold/5"}`}>
                        {deal.status === "archiviata" ? "Archiviata" : "In corso"}
                    </span>
                    <button
                        data-testid="deal-archive-btn"
                        onClick={onArchive}
                        className="cta-outline px-3 py-1.5 text-[11px] flex items-center gap-1.5"
                    >
                        {deal.status === "archiviata" ? <><ArchiveRestore size={12} /> Ripristina</> : <><Archive size={12} /> Archivia</>}
                    </button>
                    <button
                        data-testid="deal-delete-btn"
                        onClick={onDelete}
                        className="border border-red-700/40 bg-red-900/20 text-red-300 px-3 py-1.5 text-[11px] hover:bg-red-900/40 flex items-center gap-1.5"
                    >
                        <Trash2 size={12} /> Elimina
                    </button>
                </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <label className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-2">
                    Prezzo vendita
                    <input
                        data-testid="deal-price-input"
                        type="number"
                        min="0"
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        onBlur={commitPrice}
                        placeholder="—"
                        className="w-32 bg-ink-950 border border-white/15 px-2 py-1 text-ink-50 focus:border-gold outline-none"
                    />
                </label>
                {saving && <span className="text-[10px] text-ink-500">Salvataggio…</span>}
                <span className="text-[10px] text-ink-500">Creata {fmtDate(deal.created_at)}</span>
            </div>
        </div>
    );
}

function PartiesSection({ deal, refdata, onSave }) {
    return (
        <SectionCard icon={<User size={14} />} title="Parti">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <PartySelector
                    label="Venditore"
                    icon={<User size={12} className="text-gold" />}
                    value={deal.seller_contact_id || ""}
                    contact={deal._seller}
                    options={refdata.contacts}
                    onChange={(v) => onSave({ seller_contact_id: v || null })}
                    testid="deal-seller"
                />
                <PartySelector
                    label="Acquirente"
                    icon={<UserCheck size={12} className="text-gold" />}
                    value={deal.buyer_contact_id || ""}
                    contact={deal._buyer}
                    options={refdata.contacts}
                    onChange={(v) => onSave({ buyer_contact_id: v || null })}
                    testid="deal-buyer"
                />
                <div className="border border-white/10 bg-ink-950/50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-1.5 mb-2">
                        <HomeIcon size={12} className="text-gold" /> Immobile
                    </div>
                    <select
                        data-testid="deal-property"
                        value={deal.property_id || ""}
                        onChange={(e) => onSave({ property_id: e.target.value || null })}
                        className="input-vd mb-2"
                    >
                        <option value="">— Nessun immobile —</option>
                        {(refdata.properties || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.title} · {p.city}</option>
                        ))}
                    </select>
                    {deal._property && (
                        <div className="text-[11px] text-ink-300 space-y-0.5">
                            <div>{deal._property.title}</div>
                            <div className="text-ink-500">{deal._property.city}{deal._property.surface_sqm ? ` · ${deal._property.surface_sqm} m²` : ""}</div>
                            {deal._property.price && <div className="text-price">Listino: {formatPrice(deal._property.price)}</div>}
                        </div>
                    )}
                </div>
            </div>
        </SectionCard>
    );
}

function PartySelector({ label, icon, value, contact, options, onChange, testid }) {
    return (
        <div className="border border-white/10 bg-ink-950/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-1.5 mb-2">
                {icon} {label}
            </div>
            <select
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="input-vd mb-2"
            >
                <option value="">— Nessun contatto —</option>
                {options.map((c) => (
                    <option key={c.id} value={c.id}>{contactLabel(c)}{c.city ? ` · ${c.city}` : ""}</option>
                ))}
            </select>
            {contact && (
                <div className="text-[11px] text-ink-300 space-y-0.5">
                    <div>{contactLabel(contact)}</div>
                    {contact.email && <div className="text-ink-500 truncate">{contact.email}</div>}
                    {contact.phone && <div className="text-ink-500">{contact.phone}</div>}
                </div>
            )}
        </div>
    );
}

function CommissionSection({ deal, onSave }) {
    const c = deal.commission || {};
    const [mode, setMode] = useState(c.mode || "");
    const [fixed, setFixed] = useState(c.fixed_amount ?? "");
    const [pct, setPct] = useState(c.percentage ?? "");
    const [notes, setNotes] = useState(c.notes ?? "");
    useEffect(() => {
        setMode(c.mode || "");
        setFixed(c.fixed_amount ?? "");
        setPct(c.percentage ?? "");
        setNotes(c.notes ?? "");
    }, [deal.id, c.mode, c.fixed_amount, c.percentage, c.notes]);
    const save = () => {
        onSave({
            commission: {
                mode: mode || null,
                fixed_amount: fixed === "" ? null : parseFloat(fixed),
                percentage: pct === "" ? null : parseFloat(pct),
                notes: notes || null,
            },
        });
    };
    // Calcolo live: prezzo vendita corrente × percentuale corrente
    const liveCalculated = useMemo(() => {
        const price = parseFloat(deal.sale_price);
        const percentage = parseFloat(pct);
        if (!Number.isFinite(price) || !Number.isFinite(percentage) || price <= 0 || percentage <= 0) return null;
        return Math.round((price * percentage / 100) * 100) / 100;
    }, [deal.sale_price, pct]);
    return (
        <SectionCard icon={<Percent size={14} />} title="Provvigione">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                    <label className="label-eyebrow">Modalità</label>
                    <select data-testid="deal-commission-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="input-vd">
                        <option value="">—</option>
                        <option value="fixed">Fissa (€)</option>
                        <option value="percentage">Percentuale (%)</option>
                        <option value="both">Entrambe</option>
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow">Importo fisso (€)</label>
                    <input data-testid="deal-commission-fixed" type="number" min="0" step="0.01" value={fixed} onChange={(e) => setFixed(e.target.value)} className="input-vd" />
                </div>
                <div>
                    <label className="label-eyebrow">Percentuale (%)</label>
                    <input data-testid="deal-commission-pct" type="number" min="0" step="0.01" value={pct} onChange={(e) => setPct(e.target.value)} className="input-vd" />
                </div>
                <div>
                    <label className="label-eyebrow">Calcolata su prezzo</label>
                    <div data-testid="deal-commission-calc" className="input-vd flex items-center text-gold font-serif">
                        {liveCalculated != null ? formatEUR2(liveCalculated) : "—"}
                    </div>
                </div>
            </div>
            <div className="mt-3">
                <label className="label-eyebrow">Note provvigione</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={save} className="input-vd" placeholder="Es. 50/50 tra le parti, iva esclusa..." />
            </div>
            <div className="flex justify-end mt-3">
                <button data-testid="deal-commission-save" onClick={save} className="cta-outline px-4 py-1.5 text-[11px] flex items-center gap-1.5">
                    <Save size={11} /> Salva
                </button>
            </div>
        </SectionCard>
    );
}

function ChecklistSection({ dealId, deal, onReload }) {
    const [newTitle, setNewTitle] = useState("");
    const items = deal.checklist || [];
    const done = items.filter((i) => i.done).length;
    const pct = items.length ? Math.round(done * 100 / items.length) : 0;

    const add = async () => {
        if (!newTitle.trim()) return;
        await api.post(`/deals/${dealId}/checklist`, { title: newTitle.trim() });
        setNewTitle("");
        onReload();
    };
    const toggle = async (item) => {
        await api.patch(`/deals/${dealId}/checklist/${item.id}`, { done: !item.done });
        onReload();
    };
    const remove = async (item) => {
        if (!window.confirm(`Eliminare "${item.title}"?`)) return;
        await api.delete(`/deals/${dealId}/checklist/${item.id}`);
        onReload();
    };

    return (
        <SectionCard icon={<ClipboardCheck size={14} />} title={`Checklist avanzamento (${done}/${items.length})`}>
            <div className="h-1.5 bg-ink-950 relative overflow-hidden mb-4">
                <div className={`absolute inset-y-0 left-0 ${pct === 100 ? "bg-green-400" : "bg-gold"} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <ul className="space-y-1.5">
                {items.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 group">
                        <button
                            data-testid={`chk-toggle-${it.id}`}
                            onClick={() => toggle(it)}
                            className={`w-5 h-5 border flex items-center justify-center shrink-0 ${it.done ? "bg-green-900/40 border-green-500/50 text-green-300" : "border-white/25 text-transparent hover:border-gold"}`}
                        >
                            <Check size={12} />
                        </button>
                        <span className={`flex-1 text-sm ${it.done ? "text-ink-500 line-through" : "text-ink-100"}`}>
                            {it.title}
                        </span>
                        {it.done_at && <span className="text-[10px] text-ink-500">{fmtDate(it.done_at)}</span>}
                        <button onClick={() => remove(it)} data-testid={`chk-delete-${it.id}`} className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-red-300 transition-opacity" aria-label="Elimina voce">
                            <Trash2 size={12} />
                        </button>
                    </li>
                ))}
                {items.length === 0 && <li className="text-xs text-ink-500 italic">Nessuna voce checklist. Aggiungine una qui sotto.</li>}
            </ul>
            <div className="flex items-center gap-2 mt-4">
                <input
                    data-testid="deal-checklist-new"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder="Aggiungi nuova fase..."
                    className="flex-1 input-vd"
                />
                <button data-testid="deal-checklist-add" onClick={add} className="cta-gold px-3 py-1.5 text-[11px] flex items-center gap-1.5">
                    <Plus size={12} /> Aggiungi
                </button>
            </div>
        </SectionCard>
    );
}

function DeadlinesSection({ dealId, deal, onReload }) {
    const [title, setTitle] = useState("");
    const [date, setDate] = useState("");
    const items = (deal.deadlines || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));

    const add = async () => {
        if (!title.trim() || !date) { window.alert("Titolo e data obbligatori"); return; }
        await api.post(`/deals/${dealId}/deadlines`, { title: title.trim(), date });
        setTitle(""); setDate("");
        onReload();
    };
    const toggle = async (it) => {
        await api.patch(`/deals/${dealId}/deadlines/${it.id}`, { done: !it.done });
        onReload();
    };
    const remove = async (it) => {
        if (!window.confirm(`Eliminare scadenza "${it.title}"?`)) return;
        await api.delete(`/deals/${dealId}/deadlines/${it.id}`);
        onReload();
    };

    return (
        <SectionCard icon={<Calendar size={14} />} title={`Scadenze (${items.length})`}>
            <ul className="space-y-1.5">
                {items.map((it) => {
                    const overdue = !it.done && it.date && new Date(it.date) < new Date();
                    return (
                        <li key={it.id} className="flex items-center gap-3 group">
                            <button
                                onClick={() => toggle(it)}
                                data-testid={`deadline-toggle-${it.id}`}
                                className={`w-5 h-5 border flex items-center justify-center shrink-0 ${it.done ? "bg-green-900/40 border-green-500/50 text-green-300" : "border-white/25 text-transparent hover:border-gold"}`}
                                aria-label="Segna come completata"
                            >
                                <Check size={12} />
                            </button>
                            <span className={`flex-1 text-sm ${it.done ? "text-ink-500 line-through" : "text-ink-100"}`}>{it.title}</span>
                            <span className={`text-[10px] px-2 py-0.5 border ${overdue ? "border-red-700/40 bg-red-900/20 text-red-300" : "border-white/10 text-ink-300"}`}>
                                {fmtDate(it.date)}
                            </span>
                            <button onClick={() => remove(it)} data-testid={`deadline-delete-${it.id}`} className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-red-300 transition-opacity" aria-label="Elimina scadenza">
                                <Trash2 size={12} />
                            </button>
                        </li>
                    );
                })}
                {items.length === 0 && <li className="text-xs text-ink-500 italic">Nessuna scadenza registrata.</li>}
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2 mt-4">
                <input
                    data-testid="deal-deadline-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Es. Perizia bancaria"
                    className="input-vd"
                />
                <input
                    data-testid="deal-deadline-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="input-vd"
                />
                <button data-testid="deal-deadline-add" onClick={add} className="cta-gold px-3 py-1.5 text-[11px] flex items-center gap-1.5 justify-center">
                    <Plus size={12} /> Aggiungi
                </button>
            </div>
        </SectionCard>
    );
}

function NotaryMortgageSection({ deal, onSave }) {
    const [n, setN] = useState(deal.notary || {});
    const [m, setM] = useState(deal.mortgage || {});
    useEffect(() => { setN(deal.notary || {}); setM(deal.mortgage || {}); }, [deal.id, deal.notary, deal.mortgage]);
    const saveN = () => onSave({ notary: n });
    const saveM = () => onSave({ mortgage: m });
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SectionCard icon={<ScrollText size={14} />} title="Notaio">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Nome / Studio">
                        <input value={n.name || ""} onChange={(e) => setN({ ...n, name: e.target.value })} onBlur={saveN} className="input-vd" data-testid="deal-notary-name" />
                    </Field>
                    <Field label="Telefono">
                        <input value={n.phone || ""} onChange={(e) => setN({ ...n, phone: e.target.value })} onBlur={saveN} className="input-vd" data-testid="deal-notary-phone" />
                    </Field>
                    <Field label="Email">
                        <input value={n.email || ""} onChange={(e) => setN({ ...n, email: e.target.value })} onBlur={saveN} className="input-vd" data-testid="deal-notary-email" />
                    </Field>
                    <Field label="Data rogito">
                        <input type="date" value={n.date || ""} onChange={(e) => setN({ ...n, date: e.target.value })} onBlur={saveN} className="input-vd" data-testid="deal-notary-date" />
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label="Indirizzo studio">
                            <input value={n.address || ""} onChange={(e) => setN({ ...n, address: e.target.value })} onBlur={saveN} className="input-vd" data-testid="deal-notary-address" />
                        </Field>
                    </div>
                    <div className="sm:col-span-2">
                        <Field label="Note">
                            <textarea rows="2" value={n.notes || ""} onChange={(e) => setN({ ...n, notes: e.target.value })} onBlur={saveN} className="input-vd" />
                        </Field>
                    </div>
                </div>
            </SectionCard>

            <SectionCard icon={<Landmark size={14} />} title="Mutuo">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Banca">
                        <input value={m.bank || ""} onChange={(e) => setM({ ...m, bank: e.target.value })} onBlur={saveM} className="input-vd" data-testid="deal-mortgage-bank" />
                    </Field>
                    <Field label="Importo (€)">
                        <input type="number" min="0" value={m.amount ?? ""} onChange={(e) => setM({ ...m, amount: e.target.value })} onBlur={saveM} className="input-vd" data-testid="deal-mortgage-amount" />
                    </Field>
                    <Field label="Stato">
                        <select value={m.status || ""} onChange={(e) => setM({ ...m, status: e.target.value })} onBlur={saveM} className="input-vd" data-testid="deal-mortgage-status">
                            {MORTGAGE_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label="Note">
                            <textarea rows="2" value={m.notes || ""} onChange={(e) => setM({ ...m, notes: e.target.value })} onBlur={saveM} className="input-vd" />
                        </Field>
                    </div>
                </div>
            </SectionCard>
        </div>
    );
}

function DocumentsSection({ dealId, deal, onReload }) {
    return (
        <SectionCard icon={<FileText size={14} />} title="Documenti">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(DOC_FOLDER_LABELS).map(([key, meta]) => {
                    const Icon = meta.icon;
                    const docs = (deal.documents || []).filter((d) => d.folder === key);
                    return (
                        <DocFolder key={key} folderKey={key} label={meta.label} icon={<Icon size={12} className="text-gold" />} docs={docs} dealId={dealId} onReload={onReload} />
                    );
                })}
            </div>
        </SectionCard>
    );
}

function DocFolder({ folderKey, label, icon, docs, dealId, onReload }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const upload = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("folder", folderKey);
                fd.append("file", file);
                await api.post(`/deals/${dealId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            onReload();
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Errore caricamento");
        } finally { setUploading(false); }
    };

    const download = async (doc) => {
        try {
            const r = await api.get(`/deals/${dealId}/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { window.alert(e?.response?.data?.detail || "Errore download"); }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Eliminare "${doc.filename}"?`)) return;
        try {
            await api.delete(`/deals/${dealId}/documents/${doc.id}`);
            onReload();
        } catch (e) { window.alert(e?.response?.data?.detail || "Errore"); }
    };

    return (
        <div
            data-testid={`doc-folder-${folderKey}`}
            className={`border ${dragOver ? "border-gold bg-gold/5" : "border-white/10 bg-ink-950/50"} p-3 transition-colors`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                upload(Array.from(e.dataTransfer.files));
            }}
        >
            <div className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-1.5 mb-3">
                {icon} {label} ({docs.length})
            </div>
            <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt,.odt,.ods"
                onChange={(e) => { upload(Array.from(e.target.files)); e.target.value = ""; }}
                className="hidden"
                data-testid={`doc-file-input-${folderKey}`}
            />
            <button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid={`doc-upload-btn-${folderKey}`}
                className="w-full border border-dashed border-white/20 hover:border-gold hover:bg-white/5 py-3 text-[11px] text-ink-300 flex items-center justify-center gap-2 transition-colors mb-3"
            >
                <Upload size={12} /> {uploading ? "Caricamento..." : "Carica file (o trascina qui)"}
            </button>
            <ul className="space-y-1">
                {docs.map((d) => (
                    <li key={d.id} data-testid={`doc-item-${d.id}`} className="flex items-center gap-2 text-[11px] bg-ink-900/70 border border-white/5 px-2 py-1.5 group">
                        <FileText size={11} className="text-ink-400 shrink-0" />
                        <span className="flex-1 truncate text-ink-100" title={d.filename}>{d.filename}</span>
                        <span className="text-ink-500 text-[9px]">{Math.round((d.size || 0) / 1024)} KB</span>
                        <button onClick={() => download(d)} data-testid={`doc-download-${d.id}`} className="text-ink-400 hover:text-gold" title="Scarica"><Download size={11} /></button>
                        <button onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`} className="text-ink-400 hover:text-red-300" title="Elimina"><Trash2 size={11} /></button>
                    </li>
                ))}
                {docs.length === 0 && <li className="text-[10px] text-ink-500 italic text-center py-2">Nessun documento</li>}
            </ul>
        </div>
    );
}

function NotesSection({ deal, onSave }) {
    const [notes, setNotes] = useState(deal.notes || "");
    useEffect(() => { setNotes(deal.notes || ""); }, [deal.id, deal.notes]);
    return (
        <SectionCard icon={<ClipboardCheck size={14} />} title="Note interne">
            <textarea
                data-testid="deal-notes"
                rows="4"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== (deal.notes || "") && onSave({ notes })}
                placeholder="Appunti privati sulla pratica (visibili solo alla tua agenzia)"
                className="input-vd"
            />
        </SectionCard>
    );
}

// ============================================================
// Shared UI helpers
// ============================================================
function SectionCard({ icon, title, children }) {
    return (
        <section className="border border-white/10 bg-ink-900/40 p-4">
            <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2 mb-4">
                {icon} {title}
            </div>
            {children}
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow">{label}{required && <span className="text-gold ml-1">*</span>}</span>
            {children}
        </label>
    );
}

function ModalShell({ title, onClose, children, testid }) {
    return (
        <div data-testid={testid} className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-xl border border-white/15 bg-ink-950 p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-xl text-ink-50">{title}</h3>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-50" data-testid="modal-close" aria-label="Chiudi">
                        <X size={18} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function DrawerShell({ onClose, children, testid }) {
    return (
        <div data-testid={testid} className="fixed inset-0 z-[60] flex justify-end">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-4xl bg-ink-950 border-l border-white/10 overflow-y-auto">
                <button
                    onClick={onClose}
                    className="sticky top-3 float-right mr-3 z-10 text-ink-400 hover:text-ink-50 bg-ink-950/95 border border-white/10 p-1.5"
                    data-testid="drawer-close"
                    aria-label="Chiudi"
                >
                    <X size={16} />
                </button>
                {children}
            </div>
        </div>
    );
}
